import React, { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
import Icon from '../common/Icon';
import { formatDate, formatRelativeTime, normalizeStatus } from '../../utils/formatters';
import { studiesAPI, foldersAPI } from '../../services/api';
import { LANGUAGE_NAMES } from '../../utils/languages';
import { getTheme } from '../../utils/branding';
import './ProjectCard.css';

const ProjectCard = ({ project, onRetrySuccess, onCancelSuccess, folders = [], currentFolderId, onFolderChange }) => {
    const history = useHistory();
    const normalizedStatus = normalizeStatus(project.status);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [showFolderMenu, setShowFolderMenu] = useState(false);
    const [isMoveLoading, setIsMoveLoading] = useState(false);
    const [folderSearch, setFolderSearch] = useState('');
    const folderMenuRef = useRef(null);

    const filteredFolders = folders.filter(f =>
        f.name.toLowerCase().includes(folderSearch.toLowerCase())
    );

    const currentFolder = folders.find(f => f.folderId === currentFolderId);

    useEffect(() => {
        if (!showFolderMenu) setFolderSearch('');
    }, [showFolderMenu]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (folderMenuRef.current && !folderMenuRef.current.contains(e.target)) {
                setShowFolderMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleViewAnalysis = () => {
        history.push(`/projets/${project.studyId}`);
    };

    const handleRetry = async (e) => {
        e.stopPropagation();
        
        if (!window.confirm('Êtes-vous sûr de vouloir relancer cette analyse ? Toutes les données existantes seront supprimées.')) {
            return;
        }

        setIsRetrying(true);
        try {
            const response = await studiesAPI.retry(project.studyId);
            
            if (response && response.newStudyId) {
                history.push(`/projets/${response.newStudyId}`);
            } else if (onRetrySuccess) {
                onRetrySuccess(project.studyId);
            }
        } catch (error) {
            console.error('Error retrying study:', error);
            alert(`Erreur lors de la relance de l'étude: ${error.message}`);
        } finally {
            setIsRetrying(false);
        }
    };

    const handleCancel = async (e) => {
        e.stopPropagation();

        if (!window.confirm("Annuler cette analyse en cours ? Les donnees deja collectees seront conservees.")) {
            return;
        }

        setIsCancelling(true);
        try {
            await studiesAPI.cancel(project.studyId);
            if (onCancelSuccess) onCancelSuccess(project.studyId);
        } catch (error) {
            console.error('Error cancelling study:', error);
            alert(`Erreur lors de l'annulation : ${error.message}`);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleMoveToFolder = async (folderId) => {
        setIsMoveLoading(true);
        setShowFolderMenu(false);
        try {
            if (folderId === null) {
                await foldersAPI.removeStudy(project.studyId);
            } else {
                await foldersAPI.assignStudy(folderId, project.studyId);
            }
            if (onFolderChange) onFolderChange();
        } catch (error) {
            console.error('Erreur déplacement étude:', error);
        } finally {
            setIsMoveLoading(false);
        }
    };

    const theme = getTheme();
    const isAdsup = theme === 'adsup';

    const folderDropdown = folders.length > 0 && (
        <div className="project-folder-row" ref={folderMenuRef}>
            <button
                className={`project-folder-tag ${isMoveLoading ? 'loading' : ''}`}
                onClick={(e) => { e.stopPropagation(); setShowFolderMenu(v => !v); }}
                title="Déplacer dans un dossier"
            >
                <Icon name="Folder" size={12} />
                <span>{currentFolder ? currentFolder.name : 'Sans dossier'}</span>
                <Icon name="ChevronDown" size={12} />
            </button>
            {showFolderMenu && (
                <div className="folder-dropdown">
                    {currentFolderId && (
                        <button className="folder-dropdown-item remove" onClick={(e) => { e.stopPropagation(); handleMoveToFolder(null); }}>
                            <Icon name="X" size={13} /> Retirer du dossier
                        </button>
                    )}
                    {folders.length > 4 && (
                        <div className="folder-dropdown-search" onClick={(e) => e.stopPropagation()}>
                            <Icon name="Search" size={13} color="var(--color-gray-400)" />
                            <input className="folder-dropdown-search-input" type="text" placeholder="Rechercher..." value={folderSearch} onChange={(e) => setFolderSearch(e.target.value)} autoFocus />
                            {folderSearch && (<button className="folder-dropdown-search-clear" onClick={(e) => { e.stopPropagation(); setFolderSearch(''); }}><Icon name="X" size={11} /></button>)}
                        </div>
                    )}
                    <div className="folder-dropdown-list">
                        {filteredFolders.map(folder => (
                            <button key={folder.folderId} className={`folder-dropdown-item ${folder.folderId === currentFolderId ? 'current' : ''}`} onClick={(e) => { e.stopPropagation(); handleMoveToFolder(folder.folderId); }}>
                                <span className="folder-dot" style={{ backgroundColor: folder.color || 'var(--color-primary)' }} />
                                {folder.name}
                                {folder.folderId === currentFolderId && <Icon name="Check" size={12} />}
                            </button>
                        ))}
                        {folderSearch && filteredFolders.length === 0 && <p className="folder-dropdown-empty">Aucun dossier trouvé</p>}
                    </div>
                </div>
            )}
        </div>
    );

    const progressBar = project.status !== 'Completed' && project.status !== 'Failed' && project.completionPercentage > 0 && (
        <div className="project-progress">
            <div className="progress-header">
                <span className="progress-label">Progression</span>
                <span className="progress-value">{project.completionPercentage}%</span>
            </div>
            <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${project.completionPercentage}%` }} />
            </div>
        </div>
    );

    if (isAdsup) {
        return (
            <Card className="project-card" hover>
                <div className="project-card-top">
                    {folderDropdown}
                    {project.languageId && (
                        <span className="project-lang-badge">{LANGUAGE_NAMES[project.languageId] || project.languageId}</span>
                    )}
                </div>
                <h3 className="project-name">{project.name || 'Projet sans nom'}</h3>
                <div className="project-status-line">
                    {project.hasPendingSnapshot && (
                        <span className="pending-snapshot-badge" title="Une nouvelle analyse est en cours">
                            <Icon name="RefreshCw" size={12} className="spinning" /> Actualisation
                        </span>
                    )}
                    <StatusBadge status={project.status} errorMessage={project.errorMessage} errorType={project.errorType} />
                    <span className="project-date" title={formatDate(project.createdAt)}>
                        <Icon name="Calendar" size={12} /> {formatRelativeTime(project.createdAt)}
                    </span>
                    {project.userDisplayName && (
                        <span className="project-author"><Icon name="User" size={12} /> {project.userDisplayName}</span>
                    )}
                </div>
                <div className="project-info-row">
                    <div className="project-info-item">
                        <span className="project-label">Domaine :</span>
                        <span className="project-value">{project.domain}</span>
                    </div>
                    <div className="project-info-item">
                        <span className="project-label">Mots-cles :</span>
                        <span className="project-value">{project.keywordsCount}</span>
                    </div>
                </div>
                {progressBar}
                <div className="project-action">
                    {normalizedStatus === 'Completed' ? (
                        <Button variant="outline" fullWidth onClick={handleViewAnalysis}>Voir l'analyse</Button>
                    ) : normalizedStatus === 'Failed' ? (
                        <Button variant="outline" fullWidth onClick={handleRetry} disabled={isRetrying} icon={isRetrying ? null : <Icon name="RefreshCw" size={16} />}>
                            {isRetrying ? 'Relance en cours...' : 'Réessayer'}
                        </Button>
                    ) : normalizedStatus === 'Cancelled' ? (
                        <Button variant="outline" fullWidth onClick={handleViewAnalysis}>Voir l'analyse</Button>
                    ) : (
                        <Button variant="outline" fullWidth onClick={handleCancel} disabled={isCancelling} icon={isCancelling ? null : <Icon name="XCircle" size={16} />}>
                            {isCancelling ? 'Annulation...' : "Annuler l'analyse"}
                        </Button>
                    )}
                </div>
            </Card>
        );
    }

    return (
        <Card className="project-card" hover>
            <div className="project-header">
                <div className="project-title-row">
                    <h3 className="project-name">{project.name || 'Projet sans nom'}</h3>
                    {project.languageId && (
                        <span className="project-lang-badge">{LANGUAGE_NAMES[project.languageId] || project.languageId}</span>
                    )}
                </div>
                <div className="project-badges">
                    {project.hasPendingSnapshot && (
                        <span className="pending-snapshot-badge" title="Une nouvelle analyse est en cours">
                            <Icon name="RefreshCw" size={12} className="spinning" /> Actualisation
                        </span>
                    )}
                    <StatusBadge status={project.status} errorMessage={project.errorMessage} errorType={project.errorType} />
                </div>
            </div>
            <div className="project-info">
                <p className="project-domain">
                    <span className="project-label">Domaine:</span>
                    <span className="project-value">{project.domain}</span>
                </p>
                <p className="project-keywords">
                    <span className="project-label">Mots-clés:</span>
                    <span className="project-value">{project.keywordsCount} mots-clés</span>
                </p>
                {progressBar}
                <div className="project-footer">
                    <span className="project-updated" title={formatDate(project.createdAt)}>
                        <Icon name="Calendar" size={12} /> {formatRelativeTime(project.createdAt)}
                    </span>
                    {project.userDisplayName && (
                        <span className="project-author"><Icon name="User" size={12} /> {project.userDisplayName}</span>
                    )}
                </div>
                {folderDropdown}
            </div>
            <div className="project-action">
                {normalizedStatus === 'Completed' ? (
                    <Button variant="primary" fullWidth onClick={handleViewAnalysis}>Voir l'analyse</Button>
                ) : normalizedStatus === 'Failed' ? (
                    <Button variant="primary" fullWidth onClick={handleRetry} disabled={isRetrying} icon={isRetrying ? null : <Icon name="RefreshCw" size={16} />}>
                        {isRetrying ? 'Relance en cours...' : 'Réessayer'}
                    </Button>
                ) : normalizedStatus === 'Cancelled' ? (
                    <Button variant="outline" fullWidth onClick={handleViewAnalysis}>Voir l'analyse</Button>
                ) : (
                    <Button variant="outline" fullWidth onClick={handleCancel} disabled={isCancelling} icon={isCancelling ? null : <Icon name="XCircle" size={16} />}>
                        {isCancelling ? 'Annulation...' : "Annuler l'analyse"}
                    </Button>
                )}
            </div>
        </Card>
    );
};

export default ProjectCard;
